import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Ticket, XCircle, Users } from "lucide-react";
import api from "@/lib/api";
import { refreshPromoStatus } from "@/lib/usePromoStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const err = (e) => e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Something went wrong";
const toLocal = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16) : "");
const fromLocal = (v) => (v ? new Date(v).toISOString() : null);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

function F({ label, children }) {
  return <label className="block text-xs font-semibold text-slate-600 space-y-1"><span>{label}</span>{children}</label>;
}

function Counter({ label, c, testId }) {
  const pct = c.cap ? Math.min(100, Math.round((c.used / c.cap) * 100)) : 0;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="mt-1 flex items-baseline gap-2"><span className="text-3xl font-black text-slate-900">{c.left}</span><span className="text-sm text-slate-500">left of {c.cap}</span></div>
      <div className="mt-2 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-blue-700" style={{ width: `${pct}%` }} /></div>
      <div className="mt-1 text-[11px] text-slate-500">{c.used} used</div>
    </div>
  );
}

function Section({ title, live, active, onActive, children, id }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid={`promo-section-${id}`}>
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-slate-900">{title}</h2>
        <div className="flex items-center gap-3">
          {live !== undefined && <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${live ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{live ? "LIVE" : "OFF"}</span>}
          {onActive && <Switch checked={active} onCheckedChange={onActive} data-testid={`promo-${id}-active`} />}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">{children}</div>
    </section>
  );
}

function Num({ s, k, set, id, step = 1 }) {
  return <Input type="number" step={step} min={0} value={s[k]} onChange={(e) => set({ ...s, [k]: e.target.value })} data-testid={`promo-${id}-${k}`} />;
}

function Dates({ s, set, id }) {
  return (
    <>
      <F label="Starts (optional)"><Input type="datetime-local" value={toLocal(s.starts_at)} onChange={(e) => set({ ...s, starts_at: fromLocal(e.target.value) })} data-testid={`promo-${id}-starts`} /></F>
      <F label="Ends (optional)"><Input type="datetime-local" value={toLocal(s.ends_at)} onChange={(e) => set({ ...s, ends_at: fromLocal(e.target.value) })} data-testid={`promo-${id}-ends`} /></F>
    </>
  );
}

function LaunchSettings({ data, onSaved }) {
  const [s, setS] = useState(data.settings);
  const [busy, setBusy] = useState(false);
  useEffect(() => setS(data.settings), [data.settings]);
  const part = (k) => (v) => setS({ ...s, [k]: v });
  const save = async () => {
    setBusy(true);
    try { onSaved((await api.put("/admin/promotions/settings", s)).data); refreshPromoStatus(); toast.success("Promotions saved"); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const b = s.basic_launch, p = s.premium_launch, r = s.referral, l = s.loyalty, lp = s.list_prices, an = s.premium_anchor;
  return (
    <div className="space-y-4">
      <Section id="list" title="Full prices (no promo)">
        <F label="Basic price ($)"><Num s={lp} k="basic" set={part("list_prices")} id="list" step={0.01} /></F>
        <F label="Premium price ($)"><Num s={lp} k="premium" set={part("list_prices")} id="list" step={0.01} /></F>
        <p className="sm:col-span-2 text-xs text-slate-500 self-end">Used at checkout and everywhere prices show on the site. Launch promos, loyalty and codes discount from these.</p>
      </Section>
      <Section id="anchor" title="Premium 'On sale' badge" live={an.active} active={an.active} onActive={(v) => part("premium_anchor")({ ...an, active: v })}>
        <F label="'Was' price shown crossed out ($)"><Num s={an} k="was_price" set={part("premium_anchor")} id="anchor" step={0.01} /></F>
        <p className="sm:col-span-3 text-xs text-slate-500 self-end">Shows on the Premium card only when no Premium launch promo is running. Must be higher than the Premium price.</p>
      </Section>
      <Section id="basic" title="Basic launch promo" live={data.live.basic_launch} active={b.active} onActive={(v) => part("basic_launch")({ ...b, active: v })}>
        <F label="Free seats"><Num s={b} k="free_cap" set={part("basic_launch")} id="basic" /></F>
        <F label="Half-price seats"><Num s={b} k="half_cap" set={part("basic_launch")} id="basic" /></F>
        <F label="Half price ($)"><Num s={b} k="half_price" set={part("basic_launch")} id="basic" step={0.01} /></F>
        <div />
        <Dates s={b} set={part("basic_launch")} id="basic" />
      </Section>
      <Section id="premium" title="Premium launch promo" live={data.live.premium_launch} active={p.active} onActive={(v) => part("premium_launch")({ ...p, active: v })}>
        <F label="Free seats"><Num s={p} k="free_cap" set={part("premium_launch")} id="premium" /></F>
        <div /><Dates s={p} set={part("premium_launch")} id="premium" />
      </Section>
      <Section id="referral" title="Referral reward" live={r.active} active={r.active} onActive={(v) => part("referral")({ ...r, active: v })}>
        <F label="Referrals needed"><Num s={r} k="required" set={part("referral")} id="referral" /></F>
        <F label="% off Premium"><Num s={r} k="percent" set={part("referral")} id="referral" /></F>
      </Section>
      <Section id="loyalty" title="Basic → Premium loyalty price" live={l.active} active={l.active} onActive={(v) => part("loyalty")({ ...l, active: v })}>
        <F label="Upgrade price ($)"><Num s={l} k="price" set={part("loyalty")} id="loyalty" step={0.01} /></F>
      </Section>
      <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-800" data-testid="promo-settings-save">
        {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />} Save promotions
      </Button>
    </div>
  );
}

const BLANK = { code: "", kind: "percent", value: "", tiers: ["basic", "premium"], max_uses: "", per_user_limit: 1, starts_at: null, expires_at: null, active: true, note: "" };
const STATE_TONE = { live: "bg-emerald-50 text-emerald-700", scheduled: "bg-blue-50 text-blue-700", expired: "bg-amber-50 text-amber-700", used_up: "bg-slate-100 text-slate-600", ended: "bg-rose-50 text-rose-700" };

function CodeForm({ initial, onDone }) {
  const [f, setF] = useState(initial || BLANK);
  const [busy, setBusy] = useState(false);
  const editing = !!initial;
  const toggleTier = (t) => setF({ ...f, tiers: f.tiers.includes(t) ? f.tiers.filter((x) => x !== t) : [...f.tiers, t] });
  const submit = async () => {
    setBusy(true);
    const body = { ...f, value: Number(f.value), max_uses: f.max_uses === "" || f.max_uses == null ? null : Number(f.max_uses), per_user_limit: Number(f.per_user_limit) || 1 };
    try {
      await (editing ? api.put(`/admin/promotions/codes/${f.code}`, body) : api.post("/admin/promotions/codes", body));
      toast.success(editing ? "Code updated" : "Code created"); onDone();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="promo-code-form">
      <F label="Code"><Input value={f.code} disabled={editing} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="SUMMER25" data-testid="promo-code-code" /></F>
      <F label="Type">
        <select className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="promo-code-kind">
          <option value="percent">% off</option><option value="amount">$ off</option>
        </select>
      </F>
      <F label={f.kind === "percent" ? "Percent off" : "Dollars off"}><Input type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="promo-code-value" /></F>
      <F label="Max uses (blank = unlimited)"><Input type="number" value={f.max_uses ?? ""} onChange={(e) => setF({ ...f, max_uses: e.target.value })} data-testid="promo-code-max" /></F>
      <F label="Uses per member"><Input type="number" value={f.per_user_limit} onChange={(e) => setF({ ...f, per_user_limit: e.target.value })} data-testid="promo-code-per-user" /></F>
      <F label="Applies to">
        <div className="flex gap-3 pt-2">{["basic", "premium"].map((t) => (
          <label key={t} className="flex items-center gap-1.5 text-sm capitalize"><input type="checkbox" checked={f.tiers.includes(t)} onChange={() => toggleTier(t)} data-testid={`promo-code-tier-${t}`} />{t}</label>
        ))}</div>
      </F>
      <F label="Starts (optional)"><Input type="datetime-local" value={toLocal(f.starts_at)} onChange={(e) => setF({ ...f, starts_at: fromLocal(e.target.value) })} data-testid="promo-code-starts" /></F>
      <F label="Expires (optional)"><Input type="datetime-local" value={toLocal(f.expires_at)} onChange={(e) => setF({ ...f, expires_at: fromLocal(e.target.value) })} data-testid="promo-code-expires" /></F>
      <F label="Note (internal)"><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="promo-code-note" /></F>
      {editing && <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="promo-code-active" /> Active</label>}
      <div className="sm:col-span-3"><Button onClick={submit} disabled={busy} className="bg-blue-900 hover:bg-blue-800" data-testid="promo-code-submit">
        {busy && <Loader2 size={14} className="mr-2 animate-spin" />}{editing ? "Save changes" : "Create code"}</Button></div>
    </div>
  );
}

function Redemptions({ code, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { setRows(null); if (code) api.get(`/admin/promotions/codes/${code}/redemptions`).then((r) => setRows(r.data)); }, [code]);
  return (
    <Dialog open={!!code} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="promo-redemptions-dialog">
        <DialogHeader><DialogTitle>Who used {code}</DialogTitle></DialogHeader>
        {!rows ? <Loader2 className="animate-spin" /> : rows.length === 0 ? <p className="text-sm text-slate-500">No redemptions yet.</p> : (
          <div className="max-h-80 overflow-auto divide-y">{rows.map((r) => (
            <div key={r.session_id} className="py-2 text-sm flex justify-between gap-3"><span>{r.email}</span><span className="text-slate-500 capitalize">{r.package_id} · ${r.amount_paid}</span><span className="text-slate-400">{fmtDate(r.at)}</span></div>
          ))}</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Codes() {
  const [rows, setRows] = useState(null);
  const [mode, setMode] = useState(null);
  const [who, setWho] = useState(null);
  const load = useCallback(() => api.get("/admin/promotions/codes").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const end = async (c) => {
    if (!window.confirm(`End ${c}? Members won't be able to use it anymore.`)) return;
    try { await api.post(`/admin/promotions/codes/${c}/end`); toast.success(`${c} ended`); load(); } catch (e) { toast.error(err(e)); }
  };
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="promo-codes">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-slate-900 flex items-center gap-2"><Ticket size={16} /> Discount codes</h2>
        <Button size="sm" variant="outline" onClick={() => setMode(mode === "new" ? null : "new")} data-testid="promo-code-new"><Plus size={13} className="mr-1" /> New code</Button>
      </div>
      {mode && <div className="rounded-xl bg-slate-50 p-4"><CodeForm key={mode === "new" ? "new" : mode.code} initial={mode === "new" ? null : mode} onDone={() => { setMode(null); load(); }} /></div>}
      {!rows ? <Loader2 className="animate-spin text-slate-400" /> : rows.length === 0 ? <p className="text-sm text-slate-500">No codes yet.</p> : (
        <div className="divide-y">{rows.map((c) => (
          <div key={c.code} className="py-3 flex flex-wrap items-center gap-3" data-testid={`promo-code-row-${c.code}`}>
            <span className="font-mono font-bold text-slate-900 w-32">{c.code}</span>
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full uppercase ${STATE_TONE[c.state]}`}>{c.state.replace("_", " ")}</span>
            <span className="text-sm text-slate-700">{c.kind === "percent" ? `${c.value}% off` : `$${c.value} off`} · <span className="capitalize">{c.tiers.join(" + ")}</span></span>
            <span className="text-sm text-slate-600" data-testid={`promo-code-left-${c.code}`}>{c.uses} used · {c.left == null ? "unlimited" : `${c.left} left`}</span>
            <span className="text-xs text-slate-400 whitespace-nowrap">Expires {fmtDate(c.expires_at)}</span>
            <div className="ml-auto flex gap-1 shrink-0">
              <Button size="sm" variant="ghost" onClick={() => setWho(c.code)} data-testid={`promo-code-who-${c.code}`}><Users size={13} className="mr-1" /> Who used</Button>
              <Button size="sm" variant="outline" onClick={() => setMode(c)} data-testid={`promo-code-edit-${c.code}`}>Edit</Button>
              {c.active && <Button size="sm" variant="outline" className="text-rose-700" onClick={() => end(c.code)} data-testid={`promo-code-end-${c.code}`}><XCircle size={13} className="mr-1" /> End</Button>}
            </div>
          </div>
        ))}</div>
      )}
      <Redemptions code={who} onClose={() => setWho(null)} />
    </section>
  );
}

export default function AdminPromotions() {
  const [data, setData] = useState(null);
  const [fix, setFix] = useState(false);
  const [used, setUsed] = useState({});
  useEffect(() => { api.get("/admin/promotions").then((r) => setData(r.data)); }, []);
  if (!data) return <Loader2 className="animate-spin text-slate-400" />;
  const c = data.counters;
  const saveUsed = async () => {
    try {
      const body = Object.fromEntries(Object.entries(used).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)]));
      setData((await api.put("/admin/promotions/counters", body)).data); setFix(false); toast.success("Counts updated");
    } catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-promotions">
      <div>
        <h1 className="font-display font-black text-3xl text-slate-900">Promotions</h1>
        <p className="text-sm text-slate-600 mt-1">Live seat counts, launch promos, referral & loyalty discounts, and discount codes. Members always get the single best discount, never stacked.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Counter label="Basic · free seats" c={c.basic_free} testId="promo-counter-basic-free" />
        <Counter label="Basic · half-price seats" c={c.basic_half} testId="promo-counter-basic-half" />
        <Counter label="Premium · free seats" c={c.premium_free} testId="promo-counter-premium-free" />
      </div>
      <div>
        <button className="text-xs font-semibold text-blue-800 underline" onClick={() => setFix(!fix)} data-testid="promo-fix-counts">Correct the used counts</button>
        {fix && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            {[["basic_free_taken", "Basic free used", c.basic_free.used], ["basic_half_taken", "Basic half used", c.basic_half.used], ["premium_free_taken", "Premium free used", c.premium_free.used]].map(([k, l, v]) => (
              <F key={k} label={l}><Input type="number" min={0} className="w-32" placeholder={String(v)} value={used[k] ?? ""} onChange={(e) => setUsed({ ...used, [k]: e.target.value })} data-testid={`promo-used-${k}`} /></F>
            ))}
            <Button size="sm" onClick={saveUsed} data-testid="promo-used-save">Save counts</Button>
          </div>
        )}
      </div>
      <LaunchSettings data={data} onSaved={setData} />
      <Codes />
    </div>
  );
}
